import { FC, Fragment, createRef, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import styled, { DefaultTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faQuestion, faGraduationCap, faTrophy } from '@fortawesome/free-solid-svg-icons';

import SplitIOTreatment from '../../../components/splitio/splitioTreatment';
import NotificationDot, { INotificationDot } from './NotificationDot';
// import Badge from '../Badge';
import Logo from './Logo';
import MainNav from './MainNav';
import DropDownNav from './DropDownNav';
import RewardsButton from './RewardsIcon';
import ToggleButton from './ToggleButton';
import HelpDialog from './HelpDialog';

import splitsDictionary from '../../../services/utils/splitsDictionary';
import mixpanel from 'mixpanel-browser';

import Constants from '../../../constants/constants';

const EVENT = 'Therapy_SkillsUI_NavigationBar_Click';

const breakpoint = {
  medium: 'min-width: 1024px',
  large: 'min-width: 1280px',
};

const Container = styled.div(({ theme }) => {
  const { color } = theme;

  return `
    display: grid;
    grid-template-columns: max-content 1fr max-content max-content max-content;
    justify-content: center;
    flex-flow: row nowrap;
    justify-content: flex-start;
    background-color: ${color.grayscale[1000]};
    padding: 20px;
    box-shadow: 0 4px 12px ${color.grayscale[700]};
    z-index: 2;
    position: relative;

    @media screen and (${breakpoint.medium}) {
      grid-template-columns: max-content max-content 1fr max-content max-content;
    }

    @media screen and (${breakpoint.large}) {
      grid-template-columns: max-content 1fr 4fr 1fr max-content;
      padding: 24px 32px;
    }
  `;
});

const AcademyLink = styled(Link)(({ theme }) => {
  const { type, color } = theme;

  return `
    display: none;
    flex-flow: row nowrap;
    align-items: center;
    white-space: nowrap;
    font-size: ${type.size.regular};
    font-weight: ${type.weight.bold};
    font-family: ${type.face};
    margin-right: 2.25rem;

    @media screen and (${breakpoint.large}) {
      font-size: 1.125rem;
    }

    &:hover,
    &:active,
    &.active {
      text-decoration: none;
      color: ${color.primary.regular};
    }

    @media screen and (${breakpoint.large}) {
      display: flex;
    }
  `;
});

const HelpLinkButton = styled.button(({ theme }) => {
  const { type, color } = theme;

  return `
      display: none;
      flex-flow: row nowrap;
      align-items: center;
      white-space: nowrap;
      font-size: ${type.size.regular};
      font-weight: ${type.weight.bold};
      font-family: ${type.face};

      @media screen and (${breakpoint.large}) {
        font-size: 1.125rem;
      }

      &:hover,
      &:active,
      &.active {
        text-decoration: none;
        color: ${color.primary.regular};
      }
      
      @media screen and (${breakpoint.large}) {
        display: flex;
      }
    `;
});

const HelpSection = styled.section`
  grid-column: 3;
  grid-row: 1;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;

  @media screen and (${breakpoint.medium}) {
    grid-column: 4;
  }
`;

const Section = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  position: relative;
`;

const RewardsSection = styled(Section)`
  grid-column: 4;
  grid-row: 1;

  @media screen and (${breakpoint.medium}) {
    grid-column: 5;
  }
`;

const MenuSection = styled(Section)`
  grid-column: 5;

  @media screen and (${breakpoint.medium}) {
    grid-column: 2;
  }
`;

const MobileMenuButton = styled.button(({ theme }) => {
  const { color } = theme;

  return `
    grid-column: 3;
    display: inline-flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    border: 1px solid ${color.grayscale[0]};
    border-radius: 6px;
    width: 40px;
    height: 40px;

    @media screen and (${breakpoint.medium}) {
      display: none;
    }
  `;
});

const iconButtonStyles = (theme: DefaultTheme, hideSize: string) => {
  const { color } = theme;

  return `
    display: inline-flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    background-color: ${color.primary.regular};
    color: ${color.grayscale[1000]};
    width: 40px;
    height: 40px;
    margin-right: 1rem;

    @media screen and (${hideSize}) {
      display: none;
    }
  `;
};

const IconButton = styled.button(({ theme }) => {
  return iconButtonStyles(theme, breakpoint.large);
});

const IconButtonMedium = styled.button(({ theme }) => {
  return iconButtonStyles(theme, breakpoint.medium);
});

const IconLink = styled(Link)(({ theme }) => {
  return iconButtonStyles(theme, breakpoint.large);
});

const StyledRewardsButton = styled(RewardsButton)`
  display: none;

  @media screen and (${breakpoint.medium}) {
    display: block;
  }
`;

export interface IHeader {
  openAchievementDrawer: () => void;
  notificationDotStatus: INotificationDot;
}

const Header: FC<IHeader> = (props) => {
  const { openAchievementDrawer, notificationDotStatus } = props;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { currentUserFirstName, roleLevel } = useSelector((state: any) => state.user, shallowEqual);
  const [dropDownExpanded, setDropDownExpanded] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const toggleButtonRef = createRef<HTMLButtonElement>();
  const mobileToggleButtonRef = createRef<HTMLButtonElement>();
  // TODO: Set up typing for redux store
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { main, dropdown } = useSelector((state: any) => state.navigation);

  const dropdownLinks = main.concat(dropdown);

  const handleHelpClick = () => {
    mixpanel.track(EVENT, { Selection: 'Help' });
    setShowHelp(!showHelp);
  };

  const handleClose = () => {
    setShowHelp(false);
  };

  const handleMyRewardsClick = () => {
    mixpanel.track(EVENT, { Selection: 'My Rewards' });
    openAchievementDrawer();
  };

  const sendAcademyMixpanelEvent = () => {
    mixpanel.track(EVENT, { Selection: 'Academy' });
  };

  return (
    <Container id="site-header">
      <Section>
        <Logo to="/dashboard" altText="Restore" />
      </Section>

      <MenuSection>
        <ToggleButton
          ref={toggleButtonRef}
          extended={dropDownExpanded}
          toggleCallback={() => setDropDownExpanded(!dropDownExpanded)}
        >
          <Fragment>Welcome back, {currentUserFirstName}</Fragment>
        </ToggleButton>

        <DropDownNav
          links={dropdownLinks}
          extended={dropDownExpanded}
          toggleButtonRefs={[toggleButtonRef, mobileToggleButtonRef]}
          closeCallback={() => {
            setDropDownExpanded(false);
          }}
        />

        <MobileMenuButton onClick={() => setDropDownExpanded(!dropDownExpanded)} ref={mobileToggleButtonRef}>
          <FontAwesomeIcon icon={faBars} size="lg" />
        </MobileMenuButton>

        <div>{/* <Badge>Top balancer</Badge> */}</div>
      </MenuSection>

      <Section>
        <MainNav links={main} />
      </Section>

      <HelpSection>
        <>
          <AcademyLink onClick={sendAcademyMixpanelEvent} to="/wellness/lms">
            Academy
          </AcademyLink>

          <IconLink onClick={sendAcademyMixpanelEvent} to="/wellness/lms" title="Academy">
            <FontAwesomeIcon icon={faGraduationCap} />
          </IconLink>
        </>

        <HelpLinkButton onClick={handleHelpClick}>Help</HelpLinkButton>

        <IconButton onClick={handleHelpClick} title="Help" type="button">
          <FontAwesomeIcon icon={faQuestion} />
        </IconButton>
      </HelpSection>

      <SplitIOTreatment
        featureName={splitsDictionary.GAMIFICATION.name}
        treatmentValue="on"
        trafficType={splitsDictionary.GAMIFICATION.traffic_type}
      >
        {(treatment: string) => {
          if (
            treatment === 'on' &&
            roleLevel !== Constants.CARE_PARTNER_ROLE_LEVEL &&
            roleLevel !== Constants.CNA_ROLE_LEVEL
          ) {
            return (
              <RewardsSection>
                <NotificationDot {...notificationDotStatus}></NotificationDot>

                <StyledRewardsButton type="button" onClick={handleMyRewardsClick}>
                  My rewards
                </StyledRewardsButton>

                <IconButtonMedium onClick={handleMyRewardsClick} title="My Rewards" type="button">
                  <FontAwesomeIcon icon={faTrophy} />
                </IconButtonMedium>
              </RewardsSection>
            );
          } else {
            return null;
          }
        }}
      </SplitIOTreatment>

      <HelpDialog closeCallback={handleClose} isOpen={showHelp} />
    </Container>
  );
};

export default Header;
